import type { ReactNode } from 'react';
import { TFunction } from 'i18next';
import { Eye, FileEdit } from 'lucide-react';

import { ConsignmentLineTotals } from '@/entities/consignments';
import { DataTableColumn, TableAction } from '@/shared/ui/data-table';
import { LineMarkers, type LineMarkerFlags } from '@/shared/ui/line-markers';

import { ConsignmentLineRow } from '../model/types';

export const getConsignmentLineColumns = (
  t: TFunction,
  totals?: ConsignmentLineTotals,
  renderInventoryUnits?: (row: ConsignmentLineRow) => ReactNode,
  markerFlags?: LineMarkerFlags
): DataTableColumn<ConsignmentLineRow>[] => [
  {
    id: 'category',
    accessorKey: 'category',
    header: t('product.description.label'),
    width: 'w-[150px]',
    cell: (row) => (
      <LineMarkers line={row} {...markerFlags}>
        {[row.index, row.description, row.category].filter(Boolean).join(' - ') || '-'}
      </LineMarkers>
    )
  },
  {
    id: 'ownerReference',
    accessorKey: 'ownerReference',
    header: t('requestline.ownerItemIdentifier.label'),
    width: 'w-[150px]',
    cell: (row) => row.ownerReference || '-'
  },
  {
    id: 'inventoryUnits',
    accessorKey: 'inventoryUnits',
    header: t('requestline.ltuQty.short'),
    width: 'w-[150px]',
    cell: (row) => (renderInventoryUnits ? renderInventoryUnits(row) : row.inventoryUnits || '-')
  },
  {
    id: 'handlingUnits',
    accessorKey: 'handlingUnits',
    header: t('bundle.bundleQty.label'),
    width: 'w-[150px]',
    cell: (row) => row.handlingUnits || '-'
  },
  {
    id: 'reportingUnit',
    accessorKey: 'reportingUnit',
    header: t('reportingUnit.description.label'),
    width: 'w-[150px]',
    cell: (row) => row.reportingUnit || '-'
  },
  {
    id: 'weight',
    accessorKey: 'weight',
    header: () => (
      <span>
        {t('requestline.unitWeight.label')}
        {totals?.totalWeight && <span className="text-xs text-muted-foreground ml-1">({totals.totalWeight})</span>}
      </span>
    ),
    width: 'w-[120px]',
    cell: (row) => row.weight || '-'
  },
  {
    id: 'volume',
    accessorKey: 'volume',
    header: () => (
      <span>
        {t('requestline.unitVolume.label')}
        {totals?.totalVolume && <span className="text-xs text-muted-foreground ml-1">({totals.totalVolume})</span>}
      </span>
    ),
    width: 'w-[120px]',
    cell: (row) => row.volume || '-'
  }
];

export const getConsignmentLineActionColumn = (
  t: TFunction,
  onViewClick: (row: ConsignmentLineRow) => void,
  onEditClick?: (row: ConsignmentLineRow) => void
): TableAction<ConsignmentLineRow>[] => [
  {
    id: 'view',
    label: t('gen.view.label'),
    icon: Eye,
    onClick: onViewClick
  },
  ...(onEditClick
    ? [
        {
          id: 'edit',
          label: t('gen.edit.label'),
          icon: FileEdit,
          intent: 'action',
          onClick: onEditClick
        } satisfies TableAction<ConsignmentLineRow>
      ]
    : [])
];
